import React from 'react';
import { Check as CheckIcon, X as XIcon } from 'lucide-react';
import type { Seat } from '../../../types/api';
import { seatLabel } from '../../../utils/seatLabel';

/**
 * Realistic top-view matatu seat map.
 *
 * Only three Kenyan matatu configurations are supported; seats are placed by
 * `seat_number` in reading order (front-left first), the front row carries two
 * passengers beside the driver (right-hand drive), and the steering wheel marks
 * the driver position. Any other capacity falls back to the flat grid that the
 * caller renders instead (see `isMatatuLayout`).
 *
 *   14-seater : 2 + 3 + 3 + 3 + 3
 *   16-seater : 2 + 4 + 3 + 3 + 4
 *   11-seater : 2 + 3 + 3 + 3   (14-seater minus the last row)
 */

type Cell = 'seat' | 'driver' | 'aisle';

const LAYOUTS: Record<number, Cell[][]> = {
  11: [
    ['seat', 'seat', 'driver'],
    ['seat', 'seat', 'seat'],
    ['seat', 'seat', 'seat'],
    ['seat', 'seat', 'seat'],
  ],
  14: [
    ['seat', 'seat', 'driver'],
    ['seat', 'seat', 'seat'],
    ['seat', 'seat', 'seat'],
    ['seat', 'seat', 'seat'],
    ['seat', 'seat', 'seat'],
  ],
  16: [
    ['seat', 'seat', 'aisle', 'driver'],
    ['seat', 'seat', 'seat', 'seat'],
    ['seat', 'seat', 'aisle', 'seat'],
    ['seat', 'seat', 'aisle', 'seat'],
    ['seat', 'seat', 'seat', 'seat'],
  ],
};

export const SUPPORTED_CAPACITIES = Object.keys(LAYOUTS).map(Number);

export const isMatatuLayout = (capacity: number): boolean =>
  capacity in LAYOUTS;

interface Props {
  seats: Seat[];
  selectedSeatIds: number[];
  onToggle: (seat: Seat) => void;
}

const SteeringWheel: React.FC = () => (
  <svg viewBox="0 0 48 48" className="h-7 w-7" aria-hidden fill="none">
    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="3" />
    <circle cx="24" cy="24" r="5" fill="currentColor" />
    <path
      d="M24 29v13M24 29 8.5 36.5M24 29 39.5 36.5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M6 22a18 18 0 0 1 36 0"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

interface ChairProps {
  seat: Seat;
  selected: boolean;
  isWindow: boolean;
  order: number;
  onSelect: () => void;
}

const SeatChair: React.FC<ChairProps> = ({ seat, selected, isWindow, order, onSelect }) => {
  const available = seat.status === 'available';

  return (
    <button
      type="button"
      disabled={!available}
      onClick={onSelect}
      aria-label={`Seat ${seatLabel(seat.seat_number)}, ${
        selected ? 'selected' : seat.status
      }${isWindow ? ', window seat' : ''}`}
      style={{ animationDelay: `${order * 45}ms` }}
      className={[
        'group relative flex aspect-square w-full max-w-[58px] animate-seat-in flex-col items-center justify-center',
        'rounded-xl rounded-t-[1.1rem] border-b-4 text-sm font-bold tabular-nums transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
        available && !selected
          ? 'border-slate-300 border-b-slate-400 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 active:translate-y-0'
          : '',
        selected
          ? 'border-brand-800 border-b-brand-900 bg-brand-600 text-white shadow-md -translate-y-0.5'
          : '',
        !available && !selected
          ? 'cursor-not-allowed border-slate-300/70 border-b-slate-400/70 bg-slate-200 text-slate-400'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Headrest notch */}
      <span
        aria-hidden
        className={[
          'absolute -top-1 left-1/2 h-1.5 w-7 -translate-x-1/2 rounded-full transition-colors',
          selected
            ? 'bg-brand-700'
            : available
            ? 'bg-slate-300 group-hover:bg-brand-300'
            : 'bg-slate-300/70',
        ].join(' ')}
      />

      {selected ? (
        <CheckIcon className="h-4 w-4" aria-hidden />
      ) : !available ? (
        <XIcon className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <span>{seatLabel(seat.seat_number)}</span>
      )}

      {(selected || !available) && (
        <span className="absolute bottom-0.5 right-1.5 text-[10px] font-bold leading-none opacity-80">
          {seatLabel(seat.seat_number)}
        </span>
      )}

      {isWindow && available && !selected && (
        <span
          aria-hidden
          title="Window seat"
          className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-sky-500"
        />
      )}
    </button>
  );
};

const MatatuSeatMap: React.FC<Props> = ({ seats, selectedSeatIds, onToggle }) => {
  const capacity = seats.length;
  const layout = LAYOUTS[capacity];
  if (!layout) return null;

  const ordered = [...seats].sort((a, b) => a.seat_number - b.seat_number);
  const cols = Math.max(...layout.map((r) => r.length));

  let seatCursor = 0;
  let visualOrder = 0;

  return (
    <div className="mt-4">
      <div className="mx-auto w-full max-w-xs">
        {/* Bonnet / front of the vehicle */}
        <div className="relative mx-auto flex h-7 w-[78%] items-end justify-center">
          <div className="h-5 w-full rounded-t-[1.5rem] border border-b-0 border-slate-300 bg-gradient-to-b from-slate-100 to-slate-50" />
          <span
            aria-hidden
            className="absolute left-3 top-1.5 h-1.5 w-3 rounded-full bg-amber-300/90"
          />
          <span
            aria-hidden
            className="absolute right-3 top-1.5 h-1.5 w-3 rounded-full bg-amber-300/90"
          />
          <span className="absolute -top-0.5 text-[9px] font-bold uppercase tracking-[0.25em] text-slate-400">
            Front
          </span>
        </div>

        {/* Cabin */}
        <div className="rounded-[1.6rem] border border-slate-300 bg-gradient-to-b from-white to-slate-50 p-4 shadow-soft">
          <div className="flex flex-col gap-2.5">
            {layout.map((row, rowIdx) => (
              <div
                key={rowIdx}
                className="grid items-center gap-2.5"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
              >
                {row.map((cell, cellIdx) => {
                  if (cell === 'aisle') {
                    return <span key={cellIdx} aria-hidden />;
                  }
                  if (cell === 'driver') {
                    return (
                      <div
                        key={cellIdx}
                        className="flex aspect-square w-full max-w-[58px] animate-wheel-in items-center justify-center rounded-xl bg-slate-900 text-slate-100"
                        role="img"
                        aria-label="Driver seat"
                        title="Driver"
                      >
                        <SteeringWheel />
                      </div>
                    );
                  }
                  const seat = ordered[seatCursor++];
                  if (!seat) return <span key={cellIdx} aria-hidden />;
                  const order = visualOrder++;
                  // Window seats are the corner seats — the leftmost and
                  // rightmost column of each row, against the vehicle walls.
                  // The front row's right column is the driver, so only the
                  // door-side seat there counts as a window.
                  const isWindow = cellIdx === 0 || cellIdx === cols - 1;
                  return (
                    <SeatChair
                      key={seat.id}
                      seat={seat}
                      order={order}
                      isWindow={isWindow}
                      selected={selectedSeatIds.includes(seat.id)}
                      onSelect={() => {
                        if (seat.status !== 'available') return;
                        onToggle(seat);
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <MatatuLegend />
    </div>
  );
};

const MatatuLegend: React.FC = () => (
  <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-slate-600">
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3.5 w-3.5 rounded-md border border-slate-300 border-b-2 border-b-slate-400 bg-white" aria-hidden />
      Available
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3.5 w-3.5 rounded-md border border-brand-800 bg-brand-600" aria-hidden />
      Your seat
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-3.5 w-3.5 rounded-md border border-slate-300/70 bg-slate-200" aria-hidden />
      Occupied
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full bg-sky-500" aria-hidden />
      Window
    </span>
  </div>
);

export default MatatuSeatMap;
